import { toRaw, unref } from 'vue'
import L from 'leaflet'
import { sortBy } from 'lodash-unified'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { createTFQ } from '@map-sdk/sdk/plugins/weather/Typhoon/TFQ'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import type { TFQ } from '@map-sdk/sdk/plugins/weather/Typhoon/TFQ'
import type { LatLngExpression, LeafletMouseEvent } from 'leaflet'

// 可能的TyphoonDetails类型定义
export interface TyphoonDetails {
  chnname: string // 台风的中文名称
  enname: string // 台风的英文名称
}

export interface TyphoonConfiguration {
  typhoonLayer: L.LayerGroup // 台风相关所有图层的集合
  actualLayer: L.LayerGroup // 实际路径图层
  forecastLayer: L.LayerGroup // 预测路径图层
  currNodeLayer: L.LayerGroup<L.Circle | L.Marker> // 当前节点（台风眼）图层
  chnname: string // 台风中文名
  enname: string // 台风英文名
  // 可能还有其他配置属性
}

export interface PathPoint {
  id: string
  time: number
  latlng: L.LatLng
  windspeed: number
  windpower: number
  pressure: number
  direction: string
  speed: number
  radius_7?: number
  radius_10?: number
  radius_7_s: string
  radius_10_s: string
  isforecast: boolean
  provider: string
  forecasts?: PathPoint[]
}

// 类型定义
export interface DegRadiusInfo {
  deg: number
  radius: number
}

export interface ArcInfo {
  radius: number
  startAngle: number
  endAngle: number
  real: [number, number] // 原始角度范围
  clockwise: boolean
}
let LayerMap: MyMap
const _typhoonLayers: TyphoonConfiguration[] = [] // 存储台风图层配置的数组

// 创建一个新的台风配置对象
let layer: TyphoonConfiguration = {
  // 初始化各个图层组（Leaflet 图层）
  typhoonLayer: L.layerGroup(),
  actualLayer: L.layerGroup(),
  forecastLayer: L.layerGroup(),
  currNodeLayer: L.layerGroup(),
  chnname: 'typhoonInfo.chnname',
  enname: 'typhoonInfo.enname',
}
/**
 * 绘制台风服务
 */
export class TyphoonService {
  private _map: MyMap // Leaflet 地图实例
  private options: {
    autoLocation: boolean // 是否自动定位到台风
    showTimeTip: boolean // 是否显示时间提示
    showTimeTipMinZoom: number // 显示时间提示的最小缩放级别
  }

  constructor(map: MyMap, options?: any) {
    LayerMap = toRaw(unref(map))
    this._map = map
    this.options = {
      autoLocation: true,
      showTimeTip: true,
      showTimeTipMinZoom: 2,
    }
    L.Util.extend(this.options, options) // 合并用户自定义选项
    LayerMap.on('zoomend', this.showTimeTipByZoom, this) // 当地图缩放级别改变时触发事件
  }

  /**
   * 根据当前地图缩放级别决定是否显示时间提示
   *
   * @private
   */
  private showTimeTipByZoom() {
    let shouldShowTimeTip = false

    if (this.options.showTimeTip) {
      const currentZoomLevel = this._map.getZoom()
      shouldShowTimeTip = currentZoomLevel >= this.options.showTimeTipMinZoom

      for (const typhoonLayerConfig of _typhoonLayers) {
        if (typhoonLayerConfig) {
          const mainLayer =
            typhoonLayerConfig.currNodeLayer.getLayers()[0] as any
          // 获取台风当前位置经纬度
          let layerLatLng: any =
            'getLatLng' in mainLayer
              ? mainLayer.getLatLng()
              : mainLayer.getLatLngs()
          // 处理实际路径与预测路径上的点，并根据距离决定是否显示提示框
          const layers = [
            typhoonLayerConfig.actualLayer,
            typhoonLayerConfig.forecastLayer,
          ]
          const map = this._map
          layers.forEach((layerGroup) => {
            layerGroup.getLayers().forEach((item: any) => {
              if (item.options.bindTooltip) {
                if (shouldShowTimeTip) {
                  let isCloseEnough = false
                  const distanceXY = CanvasShipUtils.getDistanceXYByLatlngs(
                    map,
                    Array.isArray(layerLatLng) ? layerLatLng[0] : layerLatLng,
                    item.getLatLng()
                  )
                  if (distanceXY.x >= 100 || distanceXY.y >= 26) {
                    isCloseEnough = true
                    layerLatLng = item.getLatLng()
                  }
                  item[isCloseEnough ? 'openTooltip' : 'closeTooltip']()
                } else {
                  item.closeTooltip()
                }
              }
            })
          })
        }
      }
    }
  }

  showTyphoon(
    dataList: PathPoint[],
    typhoonInfo: TyphoonDetails
  ): TyphoonConfiguration {
    // 将传入的台风数据列表复制一份
    // 按照时间戳对台风数据进行升序排列
    const typhoons = sortBy(dataList, (o) => o.time)
    layer = {
      // 初始化各个图层组（Leaflet 图层）
      typhoonLayer: L.layerGroup(),
      actualLayer: L.layerGroup(),
      forecastLayer: L.layerGroup(),
      currNodeLayer: L.layerGroup(),
      chnname: typhoonInfo.chnname,
      enname: typhoonInfo.enname,
    }

    // 如果有台风数据
    if (typhoons && typhoons.length) {
      // 将新配置对象添加到台风图层列表中
      _typhoonLayers.push(layer)
      console.log(_typhoonLayers)

      // 将整个台风图层组添加到地图上
      LayerMap.addLayer(layer.typhoonLayer)

      // 获取台风数据列表中的最后一个数据项索引作为默认展示节点
      const defaultIndex = typhoons.length - 1

      // 绘制当前节点
      this._drawCurrNode(typhoons, defaultIndex, layer)

      // 绘制实际路径线
      this._drawActualLine(typhoons, defaultIndex, layer)

      // 绘制预测路径线
      this._drawForecastLine(typhoons, defaultIndex, layer)

      // 如果设置为自动定位，则调整地图视图至台风最新位置，并限制最大缩放级别为 5
      if (this.options.autoLocation) {
        let zoom = this._map.getZoom()
        if (zoom > 5) {
          zoom = 5
        }
        this._map.setView(typhoons[defaultIndex].latlng, zoom)
      }

      // 更新时间提示信息
      this.showTimeTipByZoom()
    }
    // 返回已添加到地图上的台风配置对象
    return layer
  }
  /**
   * 隐藏台风
   * @param type
   * @returns
   */
  hideTyphoon(type: 'all' | any) {
    if ('all' == type) {
      _typhoonLayers.forEach((item) => {
        item.typhoonLayer.clearLayers()
      })
      return
    }
    console.log(layer.typhoonLayer, type)
    type.typhoonLayer.clearLayers()
  }
  /**
   * 数据转换
   * @param resData
   * @returns
   */
  public parseRawTyphoon(resData: any) {
    const directionTextMap: { [key: string]: string } = {
      E: '东',
      W: '西',
      S: '南',
      N: '北',
      NE: '东北',
      NW: '西北',
      SW: '西南',
      SE: '东南',
      ENE: '东北偏东',
      NNE: '东北偏北',
      NNW: '西北偏北',
      WNW: '西北偏西',
      WSW: '西南偏西',
      SSW: '西南偏南',
      SSE: '东南偏南',
      ESE: '东南偏东',
    }

    const parseTimeString = (time: string): number => {
      time += ''
      const year = Number.parseInt(time.slice(0, 4))
      const month = Number.parseInt(time.slice(4, 6)) - 1
      const day = Number.parseInt(time.slice(6, 8))
      const hour = Number.parseInt(time.slice(8, 10))
      return new Date(year, month, day, hour + 8).getTime() / 1000
    }

    const parsedDataList: any[] = []
    const dataByTime: { [key: string]: any } = {}
    const processedData = CanvasShipUtils.objFrom180(resData, '', 0)

    for (const item of processedData) {
      let dataByTimeItem: PathPoint = dataByTime[item.time]

      if (!dataByTimeItem) {
        dataByTimeItem = {} as any
        dataByTime[item.time] = dataByTimeItem
        parsedDataList.push(dataByTimeItem)
      }

      const latLng = L.latLng(
        Number.parseFloat(`${item.lat}`),
        Number.parseFloat(`${item.lon}`)
      )

      if (item.forecast) {
        const forecastItem: PathPoint = {
          isforecast: true,
          provider: item.forecast,
          time: parseTimeString(item.time) + 3600 * Number.parseInt(item.fhour),
          latlng: latLng,
          id: item.id,
          windpower: item.grade,
          windspeed: item.mspeed,
          pressure: item.pressure,
          speed: item.kspeed,
          direction: directionTextMap[item.direction] || item.direction,
          radius_7: item.radius7,
          radius_10: item.radius10,
          radius_7_s: item.radius7_s,
          radius_10_s: item.radius10_s,
        }
        if (!dataByTimeItem.forecasts) {
          dataByTimeItem.forecasts = []
        }
        dataByTimeItem.forecasts.push(forecastItem)
      } else {
        dataByTimeItem.time = parseTimeString(item.time)
        dataByTimeItem.latlng = latLng
        dataByTimeItem.id = item.id
        dataByTimeItem.windpower = item.grade
        dataByTimeItem.windspeed = item.mspeed
        dataByTimeItem.pressure = item.pressure
        dataByTimeItem.speed = item.kspeed
        dataByTimeItem.direction =
          directionTextMap[item.direction] || item.direction
        dataByTimeItem.radius_7 = item.radius7
        dataByTimeItem.radius_10 = item.radius10
        dataByTimeItem.radius_7_s = item.radius7_s
        dataByTimeItem.radius_10_s = item.radius10_s
      }
    }

    return parsedDataList
  }
  private _drawActualLine(
    lines: PathPoint[],
    actualIndex: number,
    layer: TyphoonConfiguration
  ): void {
    const length = lines.length

    for (let i = 0; i < length; i++) {
      const line = lines[i]
      const time = new Date(line.time)

      if (i <= length - 2) {
        const latlngs: LatLngExpression[] = [line.latlng, lines[i + 1].latlng]
        layer.actualLayer.addLayer(
          L.polyline(latlngs, {
            pane: 'markerPane',
            color: this._getColor(lines[i + 1].windpower),
            weight: 2,
          })
        )
      }

      const color = this._getColor(line.windpower)
      if (i !== actualIndex) {
        const html = `<div class="box"><div class="field">${CommUtils.dataFormat(
          time,
          'MM月DD日 hh时'
        )}</div></div>`
        layer.actualLayer.addLayer(
          L.circleMarker(line.latlng, {
            color,
            weight: 1,
            fill: true,
            fillColor: color,
            fillOpacity: 1,
            radius: 4,
            pane: 'markerPane',
            bindTooltip: true,
          } as any).bindTooltip(html, {
            className: 'e-tooltip b-fff-b0',
            direction: 'right',
            offset: [6, 0],
            permanent: true,
            sticky: true,
          })
        )
      }

      const circleMarker = L.circleMarker(line.latlng, {
        color,
        weight: 1,
        fill: true,
        fillColor: color,
        fillOpacity: 0,
        radius: 10,
        opacity: 0,
        pane: 'markerPane',
        ext: i,
      } as any)

      const html1 = `
        <div class="weather-typh-tooltip">
          <div class="title">${layer.chnname}(${
        layer.enname || layer.chnname
      })</div>
          <div class="field"> 时间：${CommUtils.dataFormat(
            time,
            'YYYY年MM月DD日 hh时'
          )}</div>
          <div class="field"> 纬度：${CanvasShipUtils.latFormatter(
            line.latlng.lat
          )}</div>
          <div class="field"> 经度：${CanvasShipUtils.lngFormatter(
            line.latlng.lng
          )}</div>
          <div class="field"> 风速：${line.windspeed} 米/ 秒</div>
          <div class="field"> 风力：${line.windpower} 级</div>
          <div class="field"> 气压：${line.pressure} 百帕</div>
          <div class="field"> 移向：${line.direction}</div>
          <div class="field"> 移速：${line.speed}千米/时</div>
          <div class="field"> 七级半径：${
            line.radius_7 ? `${line.radius_7}千米` : '--'
          }</div>
          <div class="field" style="padding-bottom: 10px;">十级半径：${
            line.radius_10 ? `${line.radius_10}千米` : '--'
          }</div>
        </div>
      `
      circleMarker.bindTooltip(html1, {
        direction: 'right',
        offset: [15, 0],
        className: 'weather-typh-bindTooltip',
      })

      circleMarker.on('click', (e: LeafletMouseEvent) => {
        layer.currNodeLayer.remove()
        layer.currNodeLayer.clearLayers()
        this._drawCurrNode(lines, e.target.options.ext, layer, true)
        layer.actualLayer.clearLayers()
        layer.actualLayer.remove()
        this._drawActualLine(lines, e.target.options.ext, layer)
        layer.forecastLayer.clearLayers()
        layer.forecastLayer.remove()
        this._drawForecastLine(lines, e.target.options.ext, layer)
        this.showTimeTipByZoom()
      })

      layer.actualLayer.addLayer(circleMarker)
    }

    layer.typhoonLayer.addLayer(layer.actualLayer)
  }
  private _drawForecastLine(
    lines: PathPoint[],
    ext: number,
    layer: TyphoonConfiguration
  ): void {
    const nowLineLatlng = lines[ext].latlng
    if (lines[ext].forecasts) {
      const forecasts = lines[ext].forecasts as any
      if (forecasts.length > 0) {
        const sortLinesLatlng = forecasts.sort(
          (a: { time: number }, b: { time: number }) => a.time - b.time
        )

        for (let i = 0; i < sortLinesLatlng.length; i++) {
          const item = sortLinesLatlng[i]
          const time = new Date(item.time)
          const color = this._getColor(item.windpower)

          let latlngs: LatLngExpression[] = []
          if (i === 0) {
            latlngs = [nowLineLatlng, item.latlng]
          } else {
            latlngs = [sortLinesLatlng[i - 1].latlng, item.latlng]
          }

          layer.forecastLayer.addLayer(
            L.polyline(latlngs, {
              color,
              weight: 2,
              dashArray: '10, 5',
              dashOffset: '1',
              pane: 'markerPane',
            })
          )

          const circleMarker = L.circleMarker(item.latlng, {
            color,
            weight: 1,
            fill: true,
            fillColor: color,
            fillOpacity: 1,
            radius: 4,
            pane: 'markerPane',
            bindTooltip: true,
            ext: i,
          } as any)

          const html = `<div class="box"><div class="field">${CommUtils.dataFormat(
            time,
            'MM月DD日 hh时'
          )}</div></div>`
          circleMarker.bindTooltip(html, {
            className: 'e-tooltip b-fff-b0',
            direction: 'right',
            offset: [6, 0],
            permanent: true,
            sticky: true,
          })

          const circleMarker1 = L.circleMarker(item.latlng, {
            color,
            weight: 1,
            fill: true,
            fillColor: color,
            fillOpacity: 1,
            radius: 4,
            pane: 'markerPane',
          })

          let html1 = '中央台'
          if ('BABJ' !== item.provider) {
            html1 = '其他'
          }
          console.log(layer)
          const html2 = `
            <div class="weather-typh-tooltip">
              <div class="title">${layer.chnname}(${
            layer.enname || layer.chnname
          })</div>
              <div class="field">预报台：${html1}</div>
              <div class="field">时间：${CommUtils.dataFormat(
                time,
                'YYYY年MM月DD日 hh时'
              )}</div>
              <div class="field">纬度：${CanvasShipUtils.latFormatter(
                item.latlng.lat
              )}</div>
              <div class="field">经度：${CanvasShipUtils.lngFormatter(
                item.latlng.lng
              )}</div>
              <div class="field">风速：${item.windspeed}米/秒</div>
              <div class="field">风力：${item.windpower}级</div>
              <div class="field" style="padding-bottom: 10px;">气压：${
                item.pressure
              }百帕</div>
            </div>
          `

          circleMarker1.bindTooltip(html2, {
            direction: 'right',
            offset: [15, 0],
            className: 'weather-typh-bindTooltip',
          })

          const toolTip = circleMarker1.getTooltip() as any
          if (toolTip) {
            toolTip._initLayout()
            toolTip._container.style.padding = '0'
            toolTip._container.style.border = 'none'
          }

          layer.forecastLayer.addLayer(circleMarker)
          layer.forecastLayer.addLayer(circleMarker1)
        }

        layer.typhoonLayer.addLayer(layer.forecastLayer)
      }
    }
  }
  private _drawCurrNode(
    lines: PathPoint[],
    ext: number,
    layer: TyphoonConfiguration,
    isTimeout?: boolean
  ) {
    // 声明并初始化常量
    const nowLine = lines[ext] as any
    const nowLineTime = new Date(nowLine.time)
    const defaultConfig: L.TooltipOptions = {
      className: 'e-tooltip',
      direction: 'right',
      offset: [12, 0],
      permanent: false,
      sticky: true,
    }

    // 绘制风圈
    const drawWindCircle = (radiusKey: string, color: string) => {
      const radiusS = nowLine[radiusKey]
      const isRadiusNumeric = /^[0-9]+$/.test(radiusS)

      let circle: L.Circle | TFQ
      let html: string

      if (isRadiusNumeric) {
        const radius = 1000 * nowLine[radiusKey.replace('_s', '')]

        circle = L.circle(nowLine.latlng, {
          pane: 'markerPane',
          color,
          weight: 2,
          fillColor: color,
          fillOpacity: 0.5,
          radius,
          className: radiusKey,
        })
        html = `
        <div class="box">
          <div class="field">${radiusKey
            .replace('_s', '级')
            .replace('radius_', '')}风圈</div>
          <div class="field">半径：${radius ? `${radius}千米` : '--'}</div>
        </div>
      `
      } else {
        const directions = ['东北', '西北', '西南', '东南']
        const radiusValues = radiusS.split(',')
        const deg = ['45:', '135:', '225:', '315:']
          .map((dir, i) => dir + radiusValues[i])
          .join('|')
        const arcs = this._getArcs(deg)

        circle = createTFQ(nowLine.latlng, arcs, {
          color,
          className: radiusKey,
        })
        html = `
        <div class="box">
          <div class="field">${radiusKey
            .replace('_s', '级')
            .replace('radius_', '')}风圈</div>
          ${radiusValues
            .map(
              (val: any, i: number) =>
                `<div class="field">${directions[i]}：${
                  val ? `${val}千米` : '--'
                }</div>`
            )
            .join('')}
        </div>
      `
      }

      circle.bindTooltip(html, defaultConfig)
      layer.currNodeLayer.addLayer(circle)

      return circle
    }

    // 绘制7级风圈
    if (nowLine.radius_7) {
      drawWindCircle('radius_7_s', '#D8D243')
    }

    // 绘制10级风圈
    if (nowLine.radius_10) {
      drawWindCircle('radius_10_s', '#E59851')
    }

    // 绘制台风中心点
    const circleMarker = L.circle(nowLine.latlng, {
      pane: 'markerPane',
      radius: 0,
    })
    const circleMarkerHtml = `
    <div class="box">
      <div class="field">${layer.chnname}(${CommUtils.dataFormat(
      nowLineTime,
      'MM月DD日 hh时'
    )})</div>
    </div>
  `
    defaultConfig.permanent = false
    circleMarker.bindTooltip(circleMarkerHtml, defaultConfig)
    layer.currNodeLayer.addLayer(circleMarker)

    // 绘制台风图标
    const marker = L.marker(nowLine.latlng, {
      icon: L.icon({
        iconUrl: ShipxyOptions.weather.rotate,
        iconSize: [32, 32],
        iconAnchor: [17, 17],
      }),
      bubblingMouseEvents: true,
      pane: 'markerPane',
      zIndexOffset: 99999999,
    })

    const markerHtml = `
    <div class="weather-typh-tooltip">
      <div class="title">${layer.chnname}(${
      layer.enname || layer.chnname
    })</div>
      <div class="field"> 时间：${CommUtils.dataFormat(
        nowLineTime,
        'YYYY年MM月DD日 hh时'
      )}</div>
      <div class="field">纬度 ：${CanvasShipUtils.latFormatter(
        nowLine.latlng.lat
      )}</div>
      <div class="field">经度：${CanvasShipUtils.lngFormatter(
        nowLine.latlng.lng
      )}</div>
      <div class="field">风速：${nowLine.windspeed}米/秒</div>
      <div class="field">风力：${nowLine.windpower}级</div>
      <div class="field">气压：${nowLine.pressure}百帕</div>
      <div class="field">移向：${nowLine.direction}</div>
      <div class="field">移速：${nowLine.speed}千米/时</div>
      <div class="field">七级半径：${
        nowLine.radius_7 ? `${nowLine.radius_7}千米` : '--'
      }</div>
      <div class="field" style="padding-bottom: 10px;">十级半径：${
        nowLine.radius_10 ? `${nowLine.radius_10}千米` : '--'
      }</div>
    </div>
  `
    marker.bindTooltip(markerHtml, {
      direction: 'right',
      offset: [15, 0],
      className: 'weather-typh-bindTooltip',
    })
    layer.currNodeLayer.addLayer(marker)
    layer.typhoonLayer.addLayer(layer.currNodeLayer)

    console.log(layer, 'layer')
    // 鼠标悬停时自动打开tooltip
    if (isTimeout) {
      setTimeout(() => {
        marker.openTooltip()
      }, 200)
    }
  }
  _getArcs(degStr: string): ArcInfo[] {
    const arcs: ArcInfo[] = []
    let degRadius: DegRadiusInfo[] = []

    degStr &&
      degStr.split('|').forEach((item) => {
        const items = item.split(':')
        degRadius.push({
          deg: Number(items[0]),
          radius: Number(items[1]),
        })
      })

    degRadius.sort((a, b) => a.deg - b.deg)

    if (
      degRadius.length === 2 &&
      degRadius[0].deg === 180 &&
      degRadius[1].deg === 360
    ) {
      degRadius = [
        { deg: 45, radius: degRadius[1].radius },
        { deg: 180, radius: degRadius[0].radius },
        { deg: 315, radius: degRadius[1].radius },
      ]
    }

    degRadius.forEach((item) => {
      if (item.radius >= 0) {
        let arc: ArcInfo
        if (arcs.length === 0) {
          arc = {
            radius: 1000 * item.radius,
            startAngle: this._turnDeg(0),
            endAngle: this._turnDeg(2 * item.deg),
            real: [0, 2 * item.deg],
            clockwise: false,
          }
        } else {
          const beforeItem = arcs[arcs.length - 1]
          let real = beforeItem.real[1] + 2 * (item.deg - beforeItem.real[1])
          real > 360 && (real = 360)
          arc = {
            radius: 1000 * item.radius,
            startAngle: beforeItem.endAngle,
            endAngle: beforeItem.endAngle + (real - beforeItem.real[1]),
            real: [beforeItem.real[1], real],
            clockwise: false,
          }
        }
        arcs.push(arc)
      }
    })

    return arcs
  }
  _turnDeg(deg: number): number {
    return deg - 90
  }

  _getColor(power: number) {
    if (power >= 16) return '#AD00D8'
    switch (power * 1) {
      case 6:
      case 7:
        return '#076DEB'
      case 8:
      case 9:
        return '#FEF301'
      case 10:
      case 11:
        return '#FC8F2B'
      case 12:
      case 13:
        return '#FE0405'
      case 14:
      case 15:
        return '#FE3BA3'
      default:
        return '#f6f200'
    }
  }
}
